// components/DashboardSummary.tsx
import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { Urgency } from '../domain';
import { StatusDot, Tone, cx, focusRing } from './ui';

type TileUrgency = Exclude<Urgency, 'later'>;

const TILES: { urgency: TileUrgency; label: string; tone: Tone }[] = [
  { urgency: 'overdue', label: 'Overdue', tone: 'overdue' },
  { urgency: 'today', label: 'Due today', tone: 'today' },
  { urgency: 'thisWeek', label: 'This week', tone: 'upcoming' },
];

interface DashboardSummaryProps {
  counts: Record<TileUrgency, number> & { doneThisWeek: number };
  selected: Urgency | '';
  onToggle: (urgency: Urgency) => void;
}

/** The counts at the top of the dashboard; each of the first three also shows only its group. */
export const DashboardSummary: React.FC<DashboardSummaryProps> = ({ counts, selected, onToggle }) => (
  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 sm:gap-3">
    {TILES.map(({ urgency, label, tone }) => {
      const pressed = selected === urgency;
      const count = counts[urgency];
      return (
        <button
          key={urgency}
          type="button"
          aria-pressed={pressed}
          onClick={() => onToggle(urgency)}
          title={pressed ? 'Show every chore' : `Show only ${label.toLowerCase()}`}
          className={cx(
            'rounded-xl border bg-white px-3 py-2 text-left shadow-sm transition-colors dark:bg-gray-900 dark:shadow-none sm:px-4 sm:py-3',
            focusRing,
            pressed
              ? 'border-indigo-500 ring-1 ring-indigo-500 dark:border-indigo-400 dark:ring-indigo-400'
              : 'border-gray-200 hover:border-gray-300 dark:border-gray-800 dark:hover:border-gray-700',
          )}
        >
          <span className="flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-gray-500 dark:text-gray-400 sm:gap-2">
            <StatusDot tone={tone} />
            {label}
          </span>
          <span className={cx('mt-0.5 block text-xl font-bold sm:mt-1 sm:text-2xl tracking-tight tabular-nums', count === 0 ? 'text-gray-300 dark:text-gray-600' : 'text-gray-900 dark:text-gray-100')}>
            {count}
          </span>
        </button>
      );
    })}
    {/* Left out on a phone, where the three counts fill the row. */}
    <div className="hidden rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm dark:border-gray-800 dark:bg-gray-900 dark:shadow-none sm:block">
      <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
        <CheckCircleIcon className="h-4 w-4 text-success dark:text-emerald-400" aria-hidden="true" />
        Done this week
      </span>
      <span className="mt-1 block text-2xl font-bold tracking-tight tabular-nums text-gray-900 dark:text-gray-100">
        {counts.doneThisWeek}
        <span className="ml-1.5 text-sm font-medium tracking-normal text-gray-500 dark:text-gray-400">
          {counts.doneThisWeek === 1 ? 'chore' : 'chores'}
        </span>
      </span>
    </div>
  </div>
);

export default DashboardSummary;
